import is from '@sindresorhus/is';
import { z } from 'zod';

import companyLogoUrlDefault from './default-assets/cobrainer-logo.svg';
import dashboardHeroImageUrlDefault from './default-assets/dashboard-hero.svg';
import favIconUrlDefault from './default-assets/favicon.png';
import onboardingImageUrlDefault from './default-assets/onboarding.jpg';
import profileBackgroundImageUrlDefault from './default-assets/recruiter-profile-background.jpg';
import { idSchema } from '../data-contract/id';
import {
    hardSoftSkillProficiencyWithUnknownLevelSchema,
    languageSkillProficiencyWithUnknownLevelSchema,
} from '../data-contract/skill';
import { translationOverridesParameterSchema } from '../internationalization';

import type { ZodBoolean, ZodObject, ZodOptional } from 'zod';

export const oidcProviderSchema = z
    .object({
        loginUrl: z.url(),
    })
    .transform((provider) => ({
        loginUrl: new URL(provider.loginUrl),
    }))
    .transform((provider) => ({
        hideLoginButtons: provider.loginUrl.searchParams.has('hideLogin'),
        hideLogoutButtons: provider.loginUrl.searchParams.has('hideLogout'),
        loginUrl: provider.loginUrl,
    }));

export type OidcProvider = z.infer<typeof oidcProviderSchema>;

export const oidcConfigurationResponseSchema = z.object({
    providers: z
        .object({
            loginUrl: z.url(),
        })
        .array(),
});

export const oidcConfigurationSchema = z.object({
    providers: oidcProviderSchema.array(),
});

export type OidcConfiguration = z.infer<typeof oidcConfigurationSchema>;

export type OidcConfigurationResponse = z.infer<typeof oidcConfigurationResponseSchema>;

export const tenantAssetsResponseSchema = z.object({
    companyLogo: z.url(),
    dashboardHeroImageUrl: z.url(),
    favicon: z.url(),
    learningProviders: z.record(z.string(), z.url()),
    onboardingImageUrl: z.url(),
    profileBackgroundImageUrl: z.url(),
});

type TenantAssets = z.infer<typeof tenantAssetsResponseSchema>;

export const tenantAssetsDefault: TenantAssets = {
    companyLogo: `${globalThis.location?.origin}${companyLogoUrlDefault}`,
    dashboardHeroImageUrl: `${globalThis.location?.origin}${dashboardHeroImageUrlDefault}`,
    favicon: `${globalThis.location?.origin}${favIconUrlDefault}`,
    learningProviders: {},
    onboardingImageUrl: `${globalThis.location?.origin}${onboardingImageUrlDefault}`,
    profileBackgroundImageUrl: `${globalThis.location?.origin}${profileBackgroundImageUrlDefault}`,
};

export const tenantConfigSchema = z.object({
    jobs: z
        .object({
            contactButtonVisible: z.boolean().optional().default(true),
            emailVisible: z.boolean().optional().default(false),
            jobDetails: z.object({
                matchingScoreCriteria: z.number().optional().default(25),
            }),
        })
        .partial()
        .optional(),
});

const tenantThemeCssPropSchema = z
    .object({
        fontFamily: z.string(),
        fontSize: z.string(),
        fontWeight: z.number(),
        letterSpacing: z.string(),
        lineHeight: z.string(),
        paragraphIndent: z.string(),
        paragraphSpacing: z.string(),
        textDecoration: z.string(),
        textTransform: z.string(),
    })
    .partial();

const colorMainScheme = z
    .object({
        main: z.string(),
    })
    .partial();

export const tenantThemeSchema = z
    .object({
        palette: z
            .object({
                action: z
                    .object({
                        active: z.string(),
                        disabled: z.string(),
                        disabledBackground: z.string(),
                        focus: z.string(),
                        hover: z.string(),
                        selected: z.string(),
                    })
                    .partial(),
                background: z
                    .object({
                        default: z.string(),
                        paper: z.string(),
                    })
                    .partial(),
                candidate: colorMainScheme,
                cardborder: colorMainScheme,
                certification: colorMainScheme,
                education: colorMainScheme,
                error: colorMainScheme
                    .extend({
                        contrastText: z.string(),
                        dark: z.string(),
                        light: z.string(),
                    })
                    .partial(),
                hardskill: colorMainScheme
                    .extend({
                        contrastText: z.string(),
                    })
                    .partial(),
                headlineText: z.string(),
                info: colorMainScheme
                    .extend({
                        contrastText: z.string(),
                        dark: z.string(),
                        light: z.string(),
                    })
                    .partial(),
                infobox: colorMainScheme,
                jobposting: colorMainScheme,
                jobrole: colorMainScheme,
                language: colorMainScheme
                    .extend({
                        contrastText: z.string(),
                    })
                    .partial(),
                learningOpportunity: colorMainScheme,
                primary: colorMainScheme
                    .extend({
                        contrastText: z.string(),
                        dark: z.string(),
                        light: z.string(),
                    })
                    .partial(),
                project: colorMainScheme,
                publication: colorMainScheme,
                secondary: colorMainScheme
                    .extend({
                        contrastText: z.string(),
                        dark: z.string(),
                        light: z.string(),
                    })
                    .partial(),
                skillrecommendation: colorMainScheme
                    .extend({
                        contrastText: z.string(),
                    })
                    .partial(),
                softskill: colorMainScheme
                    .extend({
                        contrastText: z.string(),
                    })
                    .partial(),
                success: colorMainScheme
                    .extend({
                        contrastText: z.string(),
                        dark: z.string(),
                        light: z.string(),
                    })
                    .partial(),
                team: colorMainScheme,
                text: z
                    .object({
                        disabled: z.string(),
                        primary: z.string(),
                        secondary: z.string(),
                    })
                    .partial(),
                user: colorMainScheme,
                warning: colorMainScheme
                    .extend({
                        contrastText: z.string(),
                        dark: z.string(),
                        light: z.string(),
                    })
                    .partial(),
            })
            .partial(),
        typography: z
            .object({
                body1: tenantThemeCssPropSchema,
                body2: tenantThemeCssPropSchema,
                button: tenantThemeCssPropSchema,
                caption: tenantThemeCssPropSchema,
                h1: tenantThemeCssPropSchema,
                h2: tenantThemeCssPropSchema,
                h3: tenantThemeCssPropSchema,
                h4: tenantThemeCssPropSchema,
                h5: tenantThemeCssPropSchema,
                h6: tenantThemeCssPropSchema,
                overline: tenantThemeCssPropSchema,
                subtitle1: tenantThemeCssPropSchema,
                subtitle2: tenantThemeCssPropSchema,
            })
            .partial(),
    })
    .partial()
    .passthrough();

export type TenantThemeDefault = z.infer<typeof tenantThemeSchema>;

export const defaultTheme: TenantThemeDefault = {
    components: {
        MuiAppBar: { styleOverrides: { root: { borderRadius: 0 } } },
        MuiCssBaseline: {
            styleOverrides: {
                '#app': {
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100vh',
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderBottomColor: '#cccfd3',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    borderRadius: 0,
                },
            },
        },
        MuiListSubheader: {
            styleOverrides: {
                root: {
                    fontFamily: 'Inter',
                    fontSize: 12,
                    fontWeight: 500,
                    letterSpacing: 0,
                    lineHeight: '333%',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                elevation1: {
                    boxShadow: '0px 4px 8px -2px rgb(16 24 40 / 10%), 3px 2px 4px 4px rgb(16 24 40 / 6%)',
                },
                root: { borderRadius: 6 },
            },
        },
        MuiTab: { styleOverrides: { root: { opacity: 1 } } },
    },
    palette: {
        action: {
            active: '#1118278a',
            disabled: '#091c4042',
            disabledBackground: '#1118271f',
            focus: '#1118271f',
            hover: '#1118270a',
            selected: '#11182714',
        },
        background: {
            default: '#FBFBFF',
            paper: '#FBFBFF',
        },
        candidate: {
            main: '#0016da',
        },
        cardborder: {
            main: '#cccfd3',
        },
        certification: {
            main: '#ff9900',
        },
        education: {
            main: '#0019ff',
        },
        error: {
            contrastText: '#ffffff',
            dark: '#dc2626',
            light: '#f87171',
            main: '#ef4444',
        },
        hardskill: {
            contrastText: '#ffffff',
            main: '#007dff',
        },
        headlineText: '#ffffff',
        info: {
            contrastText: '#ffffff',
            dark: '#0369a1',
            light: '#0ea5e9',
            main: '#0284c7',
        },
        infobox: {
            main: '#2626260a',
        },
        jobposting: {
            main: '#e80060',
        },
        jobrole: {
            main: '#ea00d2',
        },
        language: {
            contrastText: '#ffffff',
            main: '#00bf90',
        },
        learningOpportunity: {
            main: '#4200ff',
        },
        primary: {
            contrastText: '#ffffff',
            dark: '#1d4ed8',
            light: '#3b82f6',
            main: '#2563eb',
        },
        project: {
            main: '#00b4dc',
        },
        publication: {
            main: '#00a67e',
        },
        secondary: {
            contrastText: '#ffffff',
            dark: '#1f2937',
            light: '#4b5563',
            main: '#374151',
        },
        skillrecommendation: {
            contrastText: '#ffffff',
            main: '#A1ABB5',
        },
        softskill: {
            contrastText: '#ffffff',
            main: '#6400ff',
        },
        success: {
            contrastText: '#ffffff',
            dark: '#047857',
            light: '#10b981',
            main: '#059669',
        },
        team: {
            main: '#9400DA',
        },
        text: {
            disabled: '#37415161',
            primary: '#111827',
            secondary: '#11182799',
        },
        user: {
            main: '#00b4dc',
        },
        warning: {
            contrastText: '#ffffff',
            dark: '#d97706',
            light: '#fbbf24',
            main: '#f59e0b',
        },
    },
    typography: {
        body1: {
            fontFamily: 'Inter',
            fontSize: '14px',
            fontWeight: 400,
            letterSpacing: '0%',
            lineHeight: '150%',
            paragraphIndent: '0px',
            paragraphSpacing: '14px',
            textDecoration: 'none',
            textTransform: 'none',
        },
        body2: {
            fontFamily: 'Inter',
            fontSize: '12px',
            fontWeight: 400,
            letterSpacing: '0%',
            lineHeight: '143%',
            paragraphIndent: '0px',
            paragraphSpacing: '12px',
            textDecoration: 'none',
            textTransform: 'none',
        },
        button: {
            fontFamily: 'Inter',
            fontSize: '14px',
            fontWeight: 500,
            letterSpacing: '0%',
            lineHeight: '150%',
            paragraphIndent: '0px',
            paragraphSpacing: '0px',
            textDecoration: 'none',
            textTransform: 'none',
        },
        caption: {
            fontFamily: 'Inter',
            fontSize: '12px',
            fontWeight: 400,
            letterSpacing: '0%',
            lineHeight: '166%',
            paragraphIndent: '0px',
            paragraphSpacing: '12px',
            textDecoration: 'none',
            textTransform: 'none',
        },
        h1: {
            fontFamily: 'Inter',
            fontSize: '48px',
            fontWeight: 600,
            letterSpacing: '-1%',
            lineHeight: '116.7%',
            paragraphIndent: '0px',
            paragraphSpacing: '48px',
            textDecoration: 'none',
            textTransform: 'none',
        },
        h2: {
            fontFamily: 'Inter',
            fontSize: '36px',
            fontWeight: 600,
            letterSpacing: '-1%',
            lineHeight: '120%',
            paragraphIndent: '0px',
            paragraphSpacing: '36px',
            textDecoration: 'none',
            textTransform: 'none',
        },
        h3: {
            fontFamily: 'Inter',
            fontSize: '32px',
            fontWeight: 600,
            letterSpacing: '-2%',
            lineHeight: '116.7%',
            paragraphIndent: '0px',
            paragraphSpacing: '30px',
            textDecoration: 'none',
            textTransform: 'none',
        },
        h4: {
            fontFamily: 'Inter',
            fontSize: '22px',
            fontWeight: 600,
            letterSpacing: '-2%',
            lineHeight: '150%',
            paragraphIndent: '0px',
            paragraphSpacing: '24px',
            textDecoration: 'none',
            textTransform: 'none',
        },
        h5: {
            fontFamily: 'Inter',
            fontSize: '18px',
            fontWeight: 600,
            letterSpacing: '-2%',
            lineHeight: '133.4%',
            paragraphIndent: '0px',
            paragraphSpacing: '18px',
            textDecoration: 'none',
            textTransform: 'none',
        },
        h6: {
            fontFamily: 'Inter',
            fontSize: '16px',
            fontWeight: 500,
            letterSpacing: '-2%',
            lineHeight: '150%',
            paragraphIndent: '0px',
            paragraphSpacing: '16px',
            textDecoration: 'none',
            textTransform: 'none',
        },
        overline: {
            fontFamily: 'Inter',
            fontSize: '12px',
            fontWeight: 400,
            letterSpacing: '1%',
            lineHeight: '266%',
            paragraphIndent: '0px',
            paragraphSpacing: '0px',
            textDecoration: 'none',
            textTransform: 'uppercase',
        },
        subtitle1: {
            fontFamily: 'Inter',
            fontSize: '16px',
            fontWeight: 400,
            letterSpacing: '0%',
            lineHeight: '175%',
            paragraphIndent: '0px',
            paragraphSpacing: '16px',
            textDecoration: 'none',
            textTransform: 'none',
        },
        subtitle2: {
            fontFamily: 'Inter',
            fontSize: '14px',
            fontWeight: 500,
            letterSpacing: '0%',
            lineHeight: '157%',
            paragraphIndent: '0px',
            paragraphSpacing: '14px',
            textDecoration: 'none',
            textTransform: 'none',
        },
    },
};

const popupContentSchema = z.object({
    de: z
        .object({
            content: z.string(),
        })
        .optional(),
    en: z
        .object({
            content: z.string(),
        })
        .optional(),
});

export const customizationUiConfigurationResponseSchema = z
    .object({
        assets: tenantAssetsResponseSchema.partial().optional(),
        config: tenantConfigSchema.partial().default({
            jobs: {
                contactButtonVisible: true,
                emailVisible: true,
                jobDetails: {
                    matchingScoreCriteria: 25,
                },
            },
        }),
        popupContent: popupContentSchema.optional(),
        theme: tenantThemeSchema.partial(),
        userManualUrl: z
            .url()
            .optional()
            .transform((urlString) => {
                return is.nonEmptyString(urlString) ? new URL(urlString) : undefined;
            }),
    })
    .partial();

const onboardingVisibility = z.object({
    everyone: z.object({
        isDefault: z.boolean(),
        isVisible: z.boolean(),
    }),
    private: z.object({
        isDefault: z.boolean(),
        isVisible: z.boolean(),
    }),
    recruiters: z.object({
        isDefault: z.boolean(),
        isVisible: z.boolean(),
    }),
    recruiters_all: z.object({
        isDefault: z.boolean(),
        isVisible: z.boolean(),
    }),
});

const commonConfigurationSchema = z.object({
    mixpanelProjectId: z.string().optional(),
    onboardingVisibility,
    seniorityLevelLimit: z.number().optional(),
    visibilityRoles: z.string().array().optional(),
});

const usersConfigurationSchema = z.object({
    userAmbitionCountLimit: z.number().optional(),
    userHardSoftDefaultLevel: hardSoftSkillProficiencyWithUnknownLevelSchema,
    userLanguageCountLimit: z.number(),
    userLanguageDefaultLevel: languageSkillProficiencyWithUnknownLevelSchema,
    userSkillCountLimit: z.number(),
});

const jobProfilesConfigurationSchema = z.object({
    jobProfileSkillCountLimit: z.number().optional(),
});

const jobsConfigurationSchema = z.object({
    jobShortListLimit: z.number(),
    jobSkillCountLimit: z.number(),
});

const learningOpportunitiesConfigurationSchema = z.object({
    learningOpportunityProviders: z.string().array(),
    learningOpportunitySkillCountLimit: z.number(),
    userBookmarkCountLimit: z.number(),
});

const urlSlugsSchema = z.string().array();

const tenantModeSchema = z.union([z.literal('trial'), z.literal('regular'), z.literal('deactivated')]);

const integrationsSchema = z.object({
    available: z
        .union([z.literal('successfactors'), z.literal('personio'), z.literal('workday'), z.literal('oracle')])
        .array(),
});

export const customizationResponseSchema: ZodObject<{
    readonly id: typeof idSchema;
    readonly commonConfiguration: typeof commonConfigurationSchema;
    readonly defaultLevelingFramework: z.ZodUUID;
    readonly integrations: typeof integrationsSchema;
    readonly isDefault: ZodBoolean;
    readonly jobProfilesConfiguration: typeof jobProfilesConfigurationSchema;
    readonly jobsConfiguration: typeof jobsConfigurationSchema;
    readonly learningOpportunitiesConfiguration: typeof learningOpportunitiesConfigurationSchema;
    readonly oidcConfiguration: typeof oidcConfigurationResponseSchema;
    readonly tenantCreatedAt: z.ZodOptional<z.ZodPipe<z.ZodISODateTime, z.ZodTransform<Date, string>>>;
    readonly tenantId: typeof idSchema;
    readonly tenantMode: typeof tenantModeSchema;
    readonly tenantTerminatedAt: z.ZodOptional<z.ZodPipe<z.ZodISODateTime, z.ZodTransform<Date, string>>>;
    readonly translationOverrides: ZodOptional<typeof translationOverridesParameterSchema>;
    readonly uiConfiguration: typeof customizationUiConfigurationResponseSchema;
    readonly urlSlugs: typeof urlSlugsSchema;
    readonly usersConfiguration: typeof usersConfigurationSchema;
}> = z.object({
    id: idSchema,
    commonConfiguration: commonConfigurationSchema,
    defaultLevelingFramework: z.uuid(),
    integrations: integrationsSchema,
    isDefault: z.boolean(),
    jobProfilesConfiguration: jobProfilesConfigurationSchema,
    jobsConfiguration: jobsConfigurationSchema,
    learningOpportunitiesConfiguration: learningOpportunitiesConfigurationSchema,
    oidcConfiguration: oidcConfigurationResponseSchema,
    tenantCreatedAt: z.iso
        .datetime()
        .transform((date) => new Date(date))
        .optional(),
    tenantId: idSchema,
    tenantMode: tenantModeSchema,
    tenantTerminatedAt: z.iso
        .datetime()
        .transform((date) => new Date(date))
        .optional(),
    translationOverrides: translationOverridesParameterSchema.optional(),
    uiConfiguration: customizationUiConfigurationResponseSchema,
    urlSlugs: urlSlugsSchema,
    usersConfiguration: usersConfigurationSchema,
});

export type CustomizationResponse = z.infer<typeof customizationResponseSchema>;
export type OnboardingVisibility = z.infer<typeof onboardingVisibility>;
